import { Component, Inject, Injectable, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ConsentRequestsService } from '../consent-requests.service';
import {
  CreateConsentRequestInput,
  UpdateConsentRequestInput,
} from '../graphql/consent-requests';
import { formatDate } from '@angular/common';
import Either from '../../../types/either';
import { Person } from '../../../interfaces/person';
import Project from '../../../interfaces/project';
import { ConsentRequestType } from '../../../interfaces/consent-request-type';
import { Subscription } from 'rxjs';
import { ConsentRequest } from '../../../interfaces/consent-request';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { DefaultSnackBarService } from '../../../services/default-snack-bar.service';
import Permission from '../../../interfaces/permission';
import { checkPermission } from '../../../pipes/permission.pipe';

type RequestDialogData = {
  created: boolean;
  input?: ConsentRequest;
  isExisting: boolean;
};
@Component({
    selector: 'add-request-dialog',
    templateUrl: './add-request-dialog.component.html',
    styleUrls: ['./add-request-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddRequestDialogComponent {
  public isExisting: boolean = false;

  public addConsentRequestForm = new FormGroup({
    project_id: new FormControl<Project | string | null>(null),
    consent_request_type_id: new FormControl<
      ConsentRequestType | string | null
    >(null, [Validators.required]),
    details: new FormControl<string | null>(null),
    is_urgent_flag: new FormControl<boolean>(false),
    urgency_note: new FormControl<string | null>(null),
    asset_manager_id: new FormControl<Person | string | null>(null),
    fund_manager_id: new FormControl<Person | string | null>(null),
    underwriter_id: new FormControl<Person | string | null>(null),
    consent_manager_id: new FormControl<Person | string | null>(null),
    investor_relations_id: new FormControl<Person | string | null>(null),
    disposition_associate_id: new FormControl<Person | string | null>(null),
    received_date: new FormControl<string | null>(null),
    timing_date: new FormControl<string | null>(null),
    consent_status_id: new FormControl<string | null>(null),
  });
  private roleKeys = [
    'asset_manager_id',
    'fund_manager_id',
    'underwriter_id',
    'consent_manager_id',
    'investor_relations_id',
    'disposition_associate_id',
  ];
  public typeValueSub: Subscription;
  public permissionSub: Subscription;
  constructor(
    public dialogRef: MatDialogRef<AddRequestDialogComponent>,
    public consentRequestsService: ConsentRequestsService,
    private _snackBar: DefaultSnackBarService,
    @Inject(MAT_DIALOG_DATA) public data: RequestDialogData
  ) {
    if (data.input)
      this.addConsentRequestForm.setValue(this.parseConsentRequest(data.input));
    this.isExisting = data.isExisting;
    if (this.isExisting) {
      this.addConsentRequestForm.controls.project_id.disable();
    }
  }
  private parseConsentRequest(request: ConsentRequest) {
    let data = {
      project_id: request.project ?? null,
      consent_request_type_id: this.consentRequestsService.getRequestType(
        request.consent_request_type_id
      ),
      consent_status_id: request.consent_status_id,
      consent_manager_id: request.consentManager ?? null,
      investor_relations_id: request.investorRelations ?? null,
      asset_manager_id: request.assetManager ?? null,
      underwriter_id: request.underwriter ?? null,
      fund_manager_id: request.fundManager ?? null,
      disposition_associate_id: request.dispositionAssociate ?? null,
      details: request.details,
      urgency_note: request.urgency_note,
      is_urgent_flag: request.is_urgent_flag,
      received_date: request.received_date
        ? request.received_date + 'T00:00:00'
        : null,
      timing_date: request.timing_date
        ? request.timing_date + 'T00:00:00'
        : null,
    };
    return data;
  }
  ngOnInit() {
    this.typeValueSub =
      this.addConsentRequestForm.controls.consent_request_type_id.valueChanges.subscribe(
        (value: ConsentRequestType | string | null) => {
          if (value && typeof value !== 'string') {
            let data = this.addConsentRequestForm.value as any;
            for (let key of this.roleKeys) {
              if (!data[key] || typeof data[key] === 'string') {
                let camelKey = key
                  .toLowerCase()
                  .replace('_id', '')
                  .replace(/([-_])([a-z])/g, (group) =>
                    group.toUpperCase().replace('-', '').replace('_', '')
                  ) as unknown as keyof ConsentRequestType;
                if (value[camelKey]) {
                  this.addConsentRequestForm.controls[
                    key as keyof typeof this.addConsentRequestForm.controls
                  ].setValue(value[camelKey] as any);
                }
              }
            }
          }
        }
      );
    this.permissionSub = this.consentRequestsService.permission$.subscribe(
      (permission) => {
        if (permission) {
          this._checkPermissions(permission);
        }
      }
    );
  }
  private _checkPermissions(permission: Permission) {
    for (let [key, value] of Object.entries(
      this.addConsentRequestForm.controls
    )) {
      let canEdit = checkPermission(permission, '_edit', key);
      if (!canEdit && this.isExisting) {
        value.disable();
      }
    }
  }
  ngOnDestroy() {
    this.typeValueSub?.unsubscribe();
  }
  private parseParticipants(data: any): any {
    for (let [key, value] of Object.entries(data)) {
      if (this.roleKeys.indexOf(key) > -1) {
        data[key] =
          value && typeof value !== 'string' ? (value as Person).id : null;
      }
    }
    return data;
  }
  private parseFormData(): Either<
    CreateConsentRequestInput,
    UpdateConsentRequestInput
  > {
    let data = this.addConsentRequestForm.value;
    data = this.parseParticipants(data);
    data.timing_date = data.timing_date
      ? formatDate(data.timing_date, 'yyyy-MM-dd', 'en-US')
      : null;
    data.project_id =
      data.project_id && typeof data.project_id !== 'string'
        ? data.project_id.id
        : null;
    data.consent_request_type_id =
      data.consent_request_type_id &&
      typeof data.consent_request_type_id !== 'string'
        ? data.consent_request_type_id.id
        : null;
    data.received_date = data.received_date
      ? formatDate(data.received_date, 'yyyy-MM-dd', 'en-US')
      : null;

    for (let [key, value] of Object.entries(
      this.addConsentRequestForm.controls
    )) {
      if (value.disabled) {
        let dataKey = key as unknown as keyof typeof data;
        if (data.hasOwnProperty(dataKey)) {
          delete data[key as unknown as keyof typeof data];
        }
      }
    }

    return data as Either<CreateConsentRequestInput, UpdateConsentRequestInput>;
  }

  saveRequest() {
    if (this.addConsentRequestForm.valid) {
      let data: Either<CreateConsentRequestInput, UpdateConsentRequestInput> =
        this.parseFormData();
      if (this.isExisting && this.data.input?.id) {
        this.consentRequestsService
          .updateConsentRequest(
            this.data.input.id,
            data as UpdateConsentRequestInput
          )
          .subscribe(() => {
            this.dialogRef.close({ saved: true });
            this._snackBar.open('Request saved Successfully');
          });
      } else if (!this.isExisting) {
        this.consentRequestsService
          .createConsentRequest(data as CreateConsentRequestInput)
          .subscribe(() => {
            this.dialogRef.close({ saved: true });
            this._snackBar.open('Request created successfully');
          });
      }
    }
  }
}
